/**
=========================================================
* Soft UI Dashboard PRO React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useContext, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import toast from "react-hot-toast";

import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import DoneIcon from "@mui/icons-material/Done";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";

import moment from "moment";
import SuiBadge from "components/SuiBadge";
import SuiInput from "components/SuiInput";
import SuiSelect from "components/SuiSelect";
import SuiButton from "components/SuiButton";
import { useForm, Controller } from "react-hook-form";

import { storeTypeOptions, employeeOptions } from "utils/data";
import { StoreContext } from "context/store";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { capitalCase } from "capital-case";
import ReadMore from "components/UpStyled/ReadMore";

const TAG_MAX_LENGTH = 25;
const AboutOurCompany = ({
  editable = true,
  store,
  tags = [],
  listStoreTags,
  updateStore,
  addStoreTag,
  account,
}) => {
  const [editVisible, setEditVisible] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTagVisible, setNewTagVisible] = useState(false);
  const [newTag, setNewTag] = useState();

  const { register, handleSubmit, trigger, setValue, getValues, reset, errors, control } = useForm(
    {}
  );

  let myTags = [];
  if (store && store.tags) {
    myTags = store.tags.map((x) => x.value);
  }

  const tagOptions = tags
    .filter((x) => !myTags.includes(x.value))
    .map((x) => {
      return {
        label: x.value,
        value: x.id,
      };
    });

  useEffect(() => {
    if (editVisible) {
      listStoreTags();
    }
  }, [editVisible]);

  useEffect(() => {
    let myTags = [];
    if (store.tags) {
      myTags = store.tags.map((x) => ({ label: x.value, value: x.value }));
    }

    reset({
      company_type: { value: store?.metadata?.company_type, label: store?.metadata?.company_type },
      employee_amount: {
        value: store?.metadata?.employee_amount,
        label: store?.metadata?.employee_amount,
      },
      story: store?.story,
      establish_date: store.establish_date
        ? moment(store.establish_date, "YYYY").format("YYYY")
        : "",
      tags: myTags,
    });

    setSelectedTags(myTags);
    setValue("tags", myTags);
  }, [store, editVisible]);

  const handleShowEdit = (e) => {
    setEditVisible(true);
  };

  const handleSave = async (data) => {
    if (data["establish_date"]) {
      const date = moment(data["establish_date"]);
      if (!date.isValid()) {
        return toast.error("Established date is not valid!");
      }
    }

    if (!data["company_type"]) {
      return toast.error("Please select a company type!");
    }

    try {
      const payload = {
        metadata: {
          company_type: data["company_type"].value,
          employee_amount: data["employee_amount"].value,
        },
        story: data["story"],
        establish_date: data["establish_date"] ? data["establish_date"] : undefined,
        tags: data["tags"].map((t) => {
          return { id: t.value, value: t.label };
        }),
      };
      await updateStore(payload);
      toast.success("Update Company information successfully!");
      setEditVisible(false);
    } catch (err) {
      toast.error("Update Company information failed!");
    }
  };

  //----------------------------------------------------------------
  //Handle tags
  //----------------------------------------------------------------
  const handleChangeTags = (option) => {
    if (option.length > 8) {
      return toast.error("You can only have at most 8 tags!");
    }

    setSelectedTags(option);
    setValue("tags", option);
  };

  const handleRemoveTags = (e, option) => {
    e.preventDefault();
    const values = selectedTags.filter((v) => v.value != option.value);
    setSelectedTags(values);
    setValue("tags", values);
  };

  const handleShowNewTag = () => {
    setNewTagVisible(true);
  };

  const handleCloseNewTag = () => {
    setNewTagVisible(false);
  };

  const handleNewTagChange = (e) => {
    setNewTag(e.target.value);
  };

  const handleSaveNewTag = async (e) => {
    e.preventDefault();
    if (selectedTags.length > 7) {
      return toast.error("You can only have at most 8 tags!");
    }
    try {
      const { type } = await addStoreTag({ value: newTag });
      toast.success("Successfully create new tag!");
      handleChangeTags([...selectedTags, { label: type.value, value: type.id }]);
      handleCloseNewTag();
    } catch (err) {
      toast.error("Create new tag failed!");
    }
  };

  //----------------------------------------------------------------
  return (
    <Card sx={{ px: 2 }}>
      <form onSubmit={handleSubmit(handleSave)}>
        <div className="m-4 min-h-[200px]">
          <div className="my-2 flex justify-between">
            <SuiTypography variant="h6" fontWeight="medium" textTransform="capitalize">
              About Our Company
            </SuiTypography>
            {editable &&
              (editVisible ? (
                <div className="flex">
                  <Tooltip title={"Cancel"} placement="top">
                    <IconButton
                      aria-label="clear"
                      size="small"
                      onClick={() => setEditVisible(false)}
                    >
                      <ClearIcon color="error" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={"Save"} placement="top">
                    <IconButton aria-label="done" size="small" type="submit">
                      <DoneIcon color="success" />
                    </IconButton>
                  </Tooltip>
                </div>
              ) : (
                <Tooltip title={"Edit"} placement="top">
                  <IconButton aria-label="edit" size="small" onClick={handleShowEdit}>
                    <EditIcon color="dark" />
                    <SuiTypography
                      variant="h6"
                      fontWeight="bold"
                      textTransform="uppercase"
                      sx={{ ml: 1, fontSize: 14 }}
                      className="xsmall:hidden small:inline"
                    >
                      EDIT
                    </SuiTypography>
                  </IconButton>
                </Tooltip>
              ))}
          </div>
          <div>
            {editVisible ? (
              <SuiInput
                placeholder="Please tell us more about your company."
                minRows={6}
                maxRows={6}
                multiline={true}
                fullWidth={true}
                {...register(`story`)}
              />
            ) : store.story ? (
              <ReadMore
                sx={{ mb: 1 }}
                color="text"
                fontWeight="regular"
                variant="text"
                limit={1000}
              >
                {store.story}
              </ReadMore>
            ) : (
              <SuiTypography
                className="min-h-[100px] flex items-center"
                variant="h6"
                color="text"
                fontWeight="regular"
                textTransform="capitalize"
              >
                Please write your company story
              </SuiTypography>
            )}
          </div>
          <div className="mt-4">
            <SuiBox key={"TYPE"} display="flex" py={1} pr={2} className=" items-center">
              <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                Type Of Manufacturer: &nbsp;
              </SuiTypography>
              <SuiTypography variant="button" fontWeight="regular" color="text">
                {editVisible ? (
                  <Controller
                    name="company_type"
                    control={control}
                    render={({ field }) => (
                      <SuiSelect className="min-w-[300px]" {...field} options={storeTypeOptions} />
                    )}
                  />
                ) : (
                  store?.metadata?.company_type
                )}
              </SuiTypography>
            </SuiBox>
            <SuiBox key={"Employees"} display="flex" py={1} pr={2} className=" items-center">
              <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                Number of Employees: &nbsp;
              </SuiTypography>
              <SuiTypography variant="button" fontWeight="regular" color="text">
                {editVisible ? (
                  <Controller
                    name="employee_amount"
                    control={control}
                    render={({ field }) => <SuiSelect {...field} options={employeeOptions} />}
                  />
                ) : (
                  store?.metadata?.employee_amount
                )}
              </SuiTypography>
            </SuiBox>
            <SuiBox key={"Established"} display="flex" py={1} pr={2} className=" items-center">
              <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                Year Established: &nbsp;
              </SuiTypography>
              <SuiTypography variant="button" fontWeight="regular" color="text">
                {editVisible ? (
                  <div className="flex items-center">
                    <SuiInput
                      placeholder="Year"
                      type="number"
                      min={0}
                      {...register("establish_date")}
                    />
                  </div>
                ) : store.establish_date ? (
                  moment(store.establish_date, "YYYY").format("YYYY")
                ) : (
                  "-"
                )}
              </SuiTypography>
            </SuiBox>

            <SuiBox key={"Email"} display="flex" py={1} pr={2} className=" items-center">
              <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                Email: &nbsp;
              </SuiTypography>
              <SuiTypography variant="button" fontWeight="regular" color="text">
                {account?.email ? account?.email : "-"}
              </SuiTypography>
            </SuiBox>
            <SuiBox key={"Business_phone"} display="flex" py={1} pr={2} className=" items-center">
              <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                Business Phone: &nbsp;
              </SuiTypography>
              <SuiTypography variant="button" fontWeight="regular" color="text">
                {store?.metadata?.business_phone ? store?.metadata?.business_phone : "-"}
              </SuiTypography>
            </SuiBox>
            {editVisible ? (
              <div>
                <div>
                  <SuiBox key={"Tags"} display="flex" py={1} pr={2} className="flex items-center">
                    <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize">
                      Tags: &nbsp;
                    </SuiTypography>
                    <SuiTypography variant="button" fontWeight="regular" color="text">
                      <div className="flex items-center">
                        <div className="mr-4">
                          <Tooltip title={"You can select at most 8 tags!"} placement="top">
                            <Controller
                              name="tags"
                              control={control}
                              render={({ field }) => {
                                return (
                                  <SuiSelect
                                    isMulti
                                    closeMenuOnSelect={false}
                                    className="min-w-[400px]"
                                    {...field}
                                    onChange={handleChangeTags}
                                    options={tagOptions}
                                  />
                                );
                              }}
                            />
                          </Tooltip>
                        </div>
                      </div>
                    </SuiTypography>
                  </SuiBox>
                  <div className="flex items-start max-w-[500px] ml-8">
                    <div className="p-2">
                      {selectedTags.map((st, index) => (
                        <SuiBadge
                          key={index}
                          variant="contained"
                          className="mr-1 mb-1"
                          textTransform="none"
                          badgeContent={
                            <div className="flex items-center">
                              {capitalCase(st.label, { stripRegexp: /[^A-Z0-9\'\&\/-_]/gi })}
                              <div
                                className="hover:cursor-pointer"
                                onClick={(e) => handleRemoveTags(e, st)}
                              >
                                <div className="text-gray-400 ml-2">X</div>
                              </div>
                            </div>
                          }
                          container
                        />
                      ))}
                      <SuiBadge
                        className="mr-1 mb-1"
                        onClick={handleShowNewTag}
                        color="blue"
                        textTransform="none"
                        variant="contained"
                        container
                        badgeContent={
                          <div className="hover:cursor-pointer">
                            <AddIcon /> <span>Customized New Tag</span>
                          </div>
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <SuiBox key={"Tags"} display="flex" py={1} pr={2} className="flex items-center">
                <SuiTypography variant="button" fontWeight="bold" textTransform="capitalize" mr={2}>
                  Tags: &nbsp;
                </SuiTypography>
                <SuiTypography variant="button" fontWeight="regular" color="text">
                  {store?.metadata && store.metadata?.store_tags
                    ? store?.metadata?.store_tags.map((t, index) => (
                        <SuiBadge
                          key={index}
                          variant="contained"
                          className="mr-1 mb-1"
                          textTransform="none"
                          color="dark"
                          badgeContent={capitalCase(t, { stripRegexp: /[^A-Z0-9\'\&\/-_]/gi })}
                          container
                        />
                      ))
                    : null}
                </SuiTypography>
              </SuiBox>
            )}
          </div>
        </div>
      </form>
      {newTagVisible ? (
        <Dialog
          open={newTagVisible}
          onClose={handleCloseNewTag}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="sm"
          fullWidth={true}
        >
          <DialogTitle id="alert-dialog-title">Create your customized tag</DialogTitle>

          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <SuiBox
                key={"new_tag"}
                display="flex"
                py={1}
                pr={2}
                className="items-center whitespace-nowrap"
              >
                <SuiTypography
                  variant="button"
                  fontWeight="bold"
                  textTransform="capitalize"
                  verticalAlign="middle"
                  sx={{ whiteSpace: "nowrap", padding: "14px 0" }}
                >
                  New Tag:
                </SuiTypography>

                <SuiInput
                  name="new-tag"
                  required
                  placeholder={`Maxmuim ${TAG_MAX_LENGTH} characters`}
                  componentsProps={{ input: { maxlength: TAG_MAX_LENGTH } }}
                  onChange={handleNewTagChange}
                />
              </SuiBox>
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <SuiButton
              variant="gradient"
              size="medium"
              onClick={handleCloseNewTag}
              className="mr-2"
              color="light"
            >
              Cancel
            </SuiButton>
            <SuiButton size="medium" variant="gradient" color="dark" onClick={handleSaveNewTag}>
              Create
            </SuiButton>
          </DialogActions>
        </Dialog>
      ) : null}
    </Card>
  );
};

export default AboutOurCompany;
